import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Testimonials = ({ config = {} }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
const navigate = useNavigate();
  // Default config (now mergeable with admin config)
  const defaultConfig = {
    title: "What Our Customers Say",
    subtitle: "Don't just take our word for it. Here's what our happy customers have to say about their experience.",
    testimonials: [
      
    ],
    devMode: false,
    customStyles: {
      container: {
        padding: 'py-12 md:py-20',
        background: 'bg-gray-50',
        titleColor: '#111827',
        subtitleColor: '#4B5563',
        textColor: '#374151',
        starColor: '#fbbf24',
        ctaBg: '#111827',
        ctaText: '#ffffff',
      },
    },
  };

  // Merge admin config with defaults
  const finalConfig = { ...defaultConfig, ...config };
  const {
    title,
    subtitle,
    testimonials,
    customStyles,
  } = finalConfig;

  const itemsPerSlide = 3;
  const maxSlide = Math.ceil(testimonials.length / itemsPerSlide) - 1;

  // Auto-play carousel
  useEffect(() => {
    if (!autoPlay) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [autoPlay, maxSlide]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1));
    setAutoPlay(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev <= 0 ? maxSlide : prev - 1));
    setAutoPlay(false);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setAutoPlay(false);
  };

  // Calculate average rating
  const averageRating = (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1);

  // ── Smart container styling ────────────────────────────────────────────
  const containerClasses = [];
  const containerStyle = {};

  const bgValue = customStyles.container?.background || 'bg-gray-50';

  if (bgValue.startsWith('bg-') || ['bg-gray-50', 'bg-white', 'bg-gray-100'].includes(bgValue)) {
    containerClasses.push(bgValue);
  } else {
    containerStyle.background = bgValue;
    if (bgValue.includes('url(')) {
      containerStyle.backgroundSize = 'cover';
      containerStyle.backgroundPosition = 'center';
      containerStyle.backgroundRepeat = 'no-repeat';
    }
  }

  if (customStyles.container?.padding) {
    containerClasses.push(customStyles.container.padding);
  }

  const finalContainerClass = containerClasses.join(' ');

  return (
    <div className={`min-h-screen ${finalContainerClass}`} style={containerStyle}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
        <br/><br/>
          <h2 
            className="text-3xl md:text-5xl font-bold mb-4"
            style={{ color: customStyles.container?.titleColor || '#111827' }}
          >
            {title}
          </h2>
          <p 
            className="text-lg md:text-xl mb-8 max-w-2xl mx-auto"
            style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}
          >
            {subtitle}
          </p>

          {/* Stats */}
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8 mt-8">
            <div className="flex items-center gap-2">
              {/*<div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className="w-5 h-5"
                    style={{ 
                      fill: customStyles.container?.starColor || '#fbbf24',
                      color: customStyles.container?.starColor || '#fbbf24'
                    }} 
                  />
                ))}
              </div>
              <span className="text-2xl font-bold" style={{ color: customStyles.container?.titleColor || '#111827' }}>
                {averageRating}
              </span>*/}
            </div>
            <div className="h-8 w-px bg-gray-300 hidden sm:block" />
            <div className="text-gray-600" style={{ color: customStyles.container?.textColor || '#4B5563' }}>
              <span className="font-bold text-gray-900">{testimonials.length}+</span> Reviews
            </div>
           <div className="h-8 w-px bg-gray-300 hidden sm:block" />
             {/*<div className="text-gray-600" style={{ color: customStyles.container?.textColor || '#4B5563' }}>
              <span className="font-bold text-gray-900">98%</span> Satisfaction Rate
            </div>*/}
          </div>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Navigation Arrows - Desktop */}
          <button
            onClick={prevSlide}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 w-12 h-12 bg-white border-2 border-gray-300 hover:border-gray-900 rounded-full items-center justify-center transition-all shadow-lg z-10"
          >
            <ChevronLeft className="w-6 h-6 text-gray-900" />
          </button>
          <button
            onClick={nextSlide}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 w-12 h-12 bg-white border-2 border-gray-300 hover:border-gray-900 rounded-full items-center justify-center transition-all shadow-lg z-10"
          >
            <ChevronRight className="w-6 h-6 text-gray-900" />
          </button>

          {/* Testimonials Container */}
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {Array.from({ length: maxSlide + 1 }).map((_, slideIdx) => (
                <div key={slideIdx} className="min-w-full">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-5 px-3">
                    {testimonials.slice(slideIdx * itemsPerSlide, (slideIdx + 1) * itemsPerSlide).map((testimonial) => (
                      <div
                        key={testimonial.id}
                        className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow relative"
                      >
                        {/* Quote Icon */}
                        <div className="absolute top-6 right-6 opacity-10">
                          <Quote className="w-16 h-16" style={{ color: customStyles.container?.textColor || '#111827' }} />
                        </div>

                        {/* Rating */}
                        <div className="flex items-center gap-1 mb-4">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className="w-5 h-5"
                              style={{ 
                                fill: i < testimonial.rating 
                                  ? (customStyles.container?.starColor || '#fbbf24') 
                                  : '#e5e7eb',
                                color: i < testimonial.rating 
                                  ? (customStyles.container?.starColor || '#fbbf24') 
                                  : '#e5e7eb'
                              }}
                            />
                          ))}
                        </div>

                        {/* Testimonial Text */}
                        <p 
                          className="leading-relaxed mb-6 relative z-10"
                          style={{ color: customStyles.container?.textColor || '#374151' }}
                        >
                          "{testimonial.testimonial}"
                        </p>

                        {/* Purchased Item */}
                        <div className="mb-6 pb-6 border-b border-gray-200">
                          <p className="text-sm" style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}>
                            Purchased: <span className="font-semibold" style={{ color: customStyles.container?.titleColor || '#111827' }}>
                              {testimonial.purchasedItem}
                            </span>
                          </p>
                        </div>

                        {/* Customer Info */}
                        <div className="flex items-center gap-4">
                          <div 
                            className={`w-14 h-14 rounded-full flex-shrink-0`}
                            style={{ background: testimonial.image }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 
                                className="font-semibold"
                                style={{ color: customStyles.container?.titleColor || '#111827' }}
                              >
                                {testimonial.name}
                              </h4>
                              {testimonial.verified && (
                                <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                              )}
                            </div>
                            <p 
                              className="text-sm"
                              style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}
                            >
                              {testimonial.role}
                            </p>
                            <p 
                              className="text-xs"
                              style={{ color: customStyles.container?.textColor || '#6B7280' }}
                            >
                              {testimonial.location}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots Indicator */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {Array.from({ length: maxSlide + 1 }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToSlide(idx)}
                className={`h-2 rounded-full transition-all ${
                  idx === currentSlide ? 'bg-gray-900 w-8' : 'bg-gray-300 w-2'
                }`}
              />
            ))}
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden items-center justify-center gap-4 mt-8">
            <button
              onClick={prevSlide}
              className="w-12 h-12 bg-white border-2 border-gray-300 hover:border-gray-900 rounded-full flex items-center justify-center transition-all"
            >
              <ChevronLeft className="w-6 h-6 text-gray-900" />
            </button>
            <button
              onClick={nextSlide}
              className="w-12 h-12 bg-white border-2 border-gray-300 hover:border-gray-900 rounded-full flex items-center justify-center transition-all"
            >
              <ChevronRight className="w-6 h-6 text-gray-900" />
            </button>
          </div>
        </div>

        {/* CTA Section */}
        <div 
          className="mt-16 rounded-2xl p-8 md:p-12 text-center"
          style={{
            background: customStyles.container?.ctaBg || 'linear-gradient(to bottom right, #111827, #1f2937)',
            color: customStyles.container?.ctaText || '#ffffff',
          }}
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Join Our Happy Customers?
          </h3>
          <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
            Experience the quality and service that our customers love. Start shopping today and see why thousands trust us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
            onClick={()=>navigate('/shop/all')}
              className="px-8 py-4 rounded-full font-semibold transition-colors"
              style={{
                backgroundColor: customStyles.container?.ctaText || '#ffffff',
                color: customStyles.container?.ctaBg || '#111827',
              }}
            >
              Shop Now
            </button>
            
          </div>
        </div>
        <br/><br/>

        {/* Trust Badges 
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 text-center">
            <div 
              className="text-2xl font-bold mb-1"
              style={{ color: customStyles.container?.titleColor || '#111827' }}
            >
              5000+
            </div>
            <div 
              className="text-sm"
              style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}
            >
              Happy Customers
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <div 
              className="text-2xl font-bold mb-1"
              style={{ color: customStyles.container?.titleColor || '#111827' }}
            >
              10k+
            </div>
            <div 
              className="text-sm"
              style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}
            >
              Products Sold
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <div 
              className="text-2xl font-bold mb-1"
              style={{ color: customStyles.container?.titleColor || '#111827' }}
            >
              4.8★
            </div>
            <div 
              className="text-sm"
              style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}
            >
              Average Rating
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center">
            <div 
              className="text-2xl font-bold mb-1"
              style={{ color: customStyles.container?.titleColor || '#111827' }}
            >
              98%
            </div>
            <div 
              className="text-sm"
              style={{ color: customStyles.container?.subtitleColor || '#4B5563' }}
            >
              Would Recommend
            </div>
          </div>
        </div>
        */}
      </div>
    </div>
  );
};

export default Testimonials;